import React, { useEffect, useState } from "react";
import apiClient from "../apiClient";
import "./EmployeeDetails.css";

/**
 * EmployeeDetails Component
 *
 * @param {object}   props
 * @param {object}   props.employee   - The selected employee object
 * @param {function} [props.onClose]  - Optional callback to close/hide this panel
 */
const EmployeeDetails = ({ employee, onClose }) => {
  const [employeeData, setEmployeeData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [noteType, setNoteType] = useState("");

  useEffect(() => {
    if (employee && employee.user_id) {
      fetchEmployeeDetailsAndNotes(employee.user_id);
    } else {
      setEmployeeData(null);
      setNotes([]);
    }
  }, [employee]);

  const fetchEmployeeDetailsAndNotes = async (userId) => {
    try {
      setLoading(true);
      setLoadingError(null);

      const empResponse = await apiClient.get(`/api/employee-profile/${userId}`);
      setEmployeeData(empResponse.data);

      const notesResponse = await apiClient.get(`/api/notes/${userId}`);
      setNotes(notesResponse.data.notes || []);
    } catch (error) {
      console.error("Error fetching employee data or notes:", error);
      setLoadingError("Failed to load employee details or notes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      alert("Please enter note content!");
      return;
    }
    if (!noteType.trim()) {
      alert("Please select a note type!");
      return;
    }

    const createdByUserId = 1;

    try {
      const response = await apiClient.post("/api/notes", {
        employee_id: employee?.user_id,
        created_by: createdByUserId,
        note_type: noteType,
        content: newNoteContent,
        priority: "normal",
      });

      setNotes((prev) => [response.data.note, ...prev]);
      setNewNoteContent("");
      setNoteType("");
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note. Please try again.");
    }
  };

  const noEmployee = !employee || !employee.user_id;

  return (
    <div className="employee-details-panel">
      {/* Close Button (if onClose prop is provided) */}
      {onClose && (
        <button className="close-button" onClick={() => onClose()}>
          ✕
        </button>
      )}

      {/* Header */}
      {!noEmployee && <h2>Employee Details</h2>}

      {/* Loading or error states */}
      {loading && <p>Loading...</p>}
      {loadingError && <p style={{ color: "red" }}>{loadingError}</p>}

      {noEmployee ? (
        <p>No employee selected.</p>
      ) : (
        <div className="employee-details-content">
          {employeeData ? (
            <div className="employee-info">
              <p><strong>Name:</strong> {employeeData.employee_name || "N/A"}</p>
            </div>
          ) : (
            !loading && <p>No employee data loaded yet.</p>
          )}

          {/* Notes Section */}
          <div className="notes-section">
            <h3>Notes</h3>
            <div className="notes-list">
              {notes.length > 0 ? (
                notes.map((note) => (
                  <div key={note.note_id} className="note-card">
                    <p>
                      <strong>Type:</strong> {note.note_type}
                    </p>
                    <p>{note.content}</p>
                    <small>
                      <strong>Created By:</strong> {note.created_by} |{" "}
                      {new Date(note.created_at).toLocaleString()}
                    </small>
                    <p>
                      <strong>Priority:</strong> {note.priority}
                    </p>
                  </div>
                ))
              ) : (
                <p className="no-notes-message">No notes for this employee yet.</p>
              )}
            </div>

            {/* Add a Note */}
            <h4>Add a Note</h4>
            <div className="note-form">
              <label>Note Type:</label>
              <select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
              >
                <option value="">-- Select Type --</option>
                <option value="performance">Performance</option>
                <option value="attendance">Attendance</option>
                <option value="feedback">Feedback</option>
                <option value="disciplinary">Disciplinary</option>
              </select>

              <label>Note Content:</label>
              <textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Enter note details..."
              />

              <button onClick={handleAddNote}>Submit Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetails;